import React, { useEffect, useRef, useState } from 'react';
import api from '../api';
import { addLocalHistoryItem } from '../historyStorage';

export default function RecommendForm() {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const quickSituations = ['1st and 10', '2nd and short', '3rd and medium', '3rd and long', 'Red zone'];

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('This browser does not support microphone input.');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data?.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = async () => {
        setListening(false);
        stopStream();
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        chunksRef.current = [];
        await transcribeWithWhisper(blob);
      };

      recorder.start();
      setListening(true);
      setError('');
    } catch (err) {
      console.error('[STT] mic start error', err);
      setError('Microphone access was blocked.');
      setListening(false);
      stopStream();
    }
  };

  const stopRecording = () => {
    if (recorderRef.current) {
      recorderRef.current.stop();
      recorderRef.current = null;
    }
    setListening(false);
  };

  useEffect(() => () => {
    stopRecording();
    stopStream();
  }, []);

  const handleVoiceInput = () => {
    if (listening) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const blobToBase64 = async (blob) => {
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  };

  const transcribeWithWhisper = async (blob) => {
    if (!blob?.size) {
      setError('No audio was recorded.');
      return;
    }

    setTranscribing(true);
    setError('');

    try {
      const audio = await blobToBase64(blob);
      const res = await api.post('/api/stt/transcribe', {
        audio,
        mimeType: blob.type || 'audio/webm',
      });
      const transcript = res.data?.text || '';

      if (transcript) {
        setInput(transcript);
        await runRecommendation(transcript);
      } else {
        setError('No words were detected.');
      }
    } catch (err) {
      console.error('[STT] transcription error', err);
      setError('Could not turn your voice into text.');
    } finally {
      setTranscribing(false);
    }
  };

  const runRecommendation = async (textInput) => {
    setError('');
    setResult(null);
    setLoading(true);

    try {
      const text = (textInput || '').trim().toLowerCase();
      if (!text) throw new Error('Type the down and distance first.');

      const downMap = {
        '1st': 1,
        first: 1,
        '2nd': 2,
        second: 2,
        '3rd': 3,
        third: 3,
        '4th': 4,
        fourth: 4,
      };

      let down = Object.keys(downMap).find((key) => text.includes(key));
      down = down ? downMap[down] : 1;

      let distance = 'medium';
      if (text.includes('short')) distance = 'short';
      else if (text.includes('medium')) distance = 'medium';
      else if (text.includes('long')) distance = 'long';

      const token = localStorage.getItem('token');
      const res = await api.post(
        '/api/rag/recommend',
        { down, distance, situation: (textInput || '').trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(res.data);
      addLocalHistoryItem({
        teamId: res.data?.team,
        timestamp: Date.now(),
        situation: (textInput || '').trim(),
        down,
        distance,
        run: res.data?.run,
        pass: res.data?.pass,
        reasoning: res.data?.reasoning,
      }, token);
      speakResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not get a recommendation.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await runRecommendation(input);
  };

  const speakResult = (data) => {
    if (!data || !window.speechSynthesis) return;

    const synth = window.speechSynthesis;

    const buildLine = (label, details, fallback) => {
      if (details) {
        const { personnel, formation, motion, action } = details;
        const parts = [];
        if (personnel) parts.push(`Personnel ${personnel}`);
        if (formation) parts.push(`Formation ${formation}`);
        if (motion) parts.push(`Motion ${motion}`);
        if (action) parts.push(`Action ${action}`);
        if (parts.length) return `${label} ${parts.join(', ')}.`;
      }

      return fallback ? `${label} ${fallback}.` : '';
    };

    const runLine = buildLine('Run play:', data.runDetails, data.run);
    const passLine = buildLine('Pass play:', data.passDetails, data.pass);

    synth.cancel();
    [runLine, passLine]
      .filter(Boolean)
      .forEach((line) => {
        const utter = new SpeechSynthesisUtterance(line);
        utter.lang = 'en-US';
        utter.rate = 1;
        synth.speak(utter);
      });
  };

  const ResultCard = ({ title, name, details }) => (
    <div className="rounded-lg border-2 border-stone-200 bg-brand-offwhite px-5 py-5">
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-orange">{title}</p>
      <h3 className="mt-3 text-3xl font-semibold text-brand-black">{name || 'No play found'}</h3>
      {details && (
        <dl className="mt-5 grid gap-3 text-base text-stone-700">
          <div>
            <dt className="form-label">Personnel</dt>
            <dd className="mt-1">{details.personnel || 'Not listed'}</dd>
          </div>
          <div>
            <dt className="form-label">Formation</dt>
            <dd className="mt-1">{details.formation || 'Not listed'}</dd>
          </div>
          <div>
            <dt className="form-label">Motion</dt>
            <dd className="mt-1">{details.motion || 'Not listed'}</dd>
          </div>
          <div>
            <dt className="form-label">Action</dt>
            <dd className="mt-1">{details.action || 'Not listed'}</dd>
          </div>
        </dl>
      )}
    </div>
  );

  return (
    <div className="grid gap-5 xl:grid-cols-[0.95fr_1.05fr]">
      <section className="surface-card">
        <p className="eyebrow">Get A Play Call</p>
        <h2 className="mt-3 text-4xl font-semibold text-brand-black">Describe the situation.</h2>
        <p className="mt-3 text-lg leading-8 text-stone-700">
          Type something simple like `3rd and long` or tap the microphone and say it out loud.
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          {quickSituations.map((situation) => (
            <button
              key={situation}
              type="button"
              onClick={() => setInput(situation)}
              className="rounded-full border border-stone-300 bg-brand-offwhite px-4 py-2 text-sm font-semibold text-stone-700 transition hover:bg-white"
            >
              {situation}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="recommend-input" className="form-label">
              Situation
            </label>
            <textarea
              id="recommend-input"
              placeholder="Example: 2nd and short"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="input-field min-h-[140px] resize-y text-lg"
              required
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={handleVoiceInput}
              disabled={transcribing || loading}
              className={`btn-secondary justify-center ${listening ? 'border-rose-300 bg-rose-50 text-rose-700' : ''}`}
            >
              {listening ? 'Tap to stop recording' : transcribing ? 'Turning voice into text...' : 'Use microphone'}
            </button>
            <button type="submit" disabled={loading || transcribing} className="btn-primary justify-center">
              {loading ? 'Getting recommendation...' : transcribing ? 'Waiting for transcript...' : 'Get recommendation'}
            </button>
          </div>
        </form>

        <div className="mt-5 rounded-lg bg-brand-offwhite px-4 py-4 text-base text-stone-700">
          If the microphone gives trouble, skip it and just type the situation.
        </div>

        {error && (
          <p className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </p>
        )}
      </section>

      <section className="surface-card">
        <p className="eyebrow">Recommendation</p>
        <h2 className="mt-3 text-4xl font-semibold text-brand-black">Best next options</h2>
        <p className="mt-3 text-lg leading-8 text-stone-700">
          You will get one run idea and one pass idea. If your browser allows it, the app reads them aloud.
        </p>

        {!result && !loading && (
          <div className="mt-8 rounded-lg border-2 border-dashed border-stone-300 bg-brand-offwhite px-6 py-12 text-center text-lg text-stone-600">
            Enter a situation, then press `Get recommendation`.
          </div>
        )}

        {loading && (
          <div className="mt-8 rounded-lg bg-brand-offwhite px-6 py-12 text-center text-lg font-medium text-stone-700">
            Looking up the best calls...
          </div>
        )}

        {result && !loading && (
          <div className="mt-8 space-y-4">
            <ResultCard title="Run" name={result.run} details={result.runDetails} />
            <ResultCard title="Pass" name={result.pass} details={result.passDetails} />

            {result.reasoning && (
              <div className="rounded-lg border border-stone-200 bg-white px-5 py-5">
                <p className="form-label">Why These Plays</p>
                <p className="mt-2 text-base leading-7 text-stone-700">{result.reasoning}</p>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
